import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { FileText, Loader2, Pencil, Plus, Search, Trash2, Wrench } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { ServiziAB, ExtraList, isOn } from "../components/ManutenzioneServizi";

const EMPTY = { lead_id: "", ragione_sociale: "", piva: "", indirizzo_impianto: "", kwp: null, chiamata_festivi: 80, ora_tecnico: 30,
  fatturazione_canone: "alla stipula", aggiornamento_tariffe: "indice ISTAT", luogo_data: "", note: "",
  servizi: {}, punti_a: {}, punti_b: {}, n_lavaggi: 1, n_visite: 1, extra: [] };
// [campo, etichetta, chiave servizio (spunta)]
const SEZIONI = [
  ["Canone annuo (€ + IVA)", [["canone_a", "A) Manutenzione ordinaria", "A"], ["canone_b", "B) Follow up Officina Elettrica e GSE", "B"]]],
  ["Servizi drone (€ + IVA)", [["drone_l1", "Livello 1: ispezione visiva", "drone_l1"], ["drone_l1_soglia_kwp", "Livello 1: fino a kWp", "drone_l1"],
    ["drone_l2", "Livello 2: termografia", "drone_l2"], ["drone_l2_soglia_kwp", "Livello 2: fino a kWp", "drone_l2"],
    ["drone_suppl", "Supplemento oltre soglia", "drone_suppl"], ["drone_suppl_step_kwp", "Supplemento ogni kWp", "drone_suppl"], ["drone_pacchetto", "Pacchetto L1 + L2", "drone_pacchetto"]]],
  ["Straordinari e tariffario (€ + IVA)", [["chiamata_feriale", "Chiamata feriale", "chiamata_feriale"], ["chiamata_festivi", "Uscita festivi", "chiamata_festivi"],
    ["chiamata_notturno", "Uscita notturna", "chiamata_notturno"], ["ora_tecnico", "Ora tecnico specializzato", "ora_tecnico"], ["ora_operaio", "Ora operaio / aiuto", "ora_operaio"],
    ["ora_quota", "Ora lavori in quota", "ora_quota"], ["km_tariffa", "Tariffa al km", "km_tariffa"], ["km_soglia", "Trasferta oltre km", "km_tariffa"],
    ["lavaggio_kwp", "Lavaggio extra per kWp", "lavaggio_kwp"], ["ricarico_pct", "Ricarico materiali %", "ricarico"], ["diagnosi_ora", "Diagnosi strumentale / ora", "diagnosi_ora"],
    ["minimo_ore", "Minimo fatturazione (ore)"]]],
];
const eur = (v) => (v == null ? "—" : `€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })}`);
const numOrNull = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));

function ClienteSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/manutenzioni/clienti", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente CRM (nome, ragione sociale, P.IVA)…" data-testid="manut-cliente-search" />
      {res.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded border bg-popover shadow-lg max-h-64 overflow-auto" data-testid="manut-cliente-results">
          {res.map((c) => (
            <button key={c.lead_id} type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-secondary" data-testid={`manut-cliente-${c.lead_id}`}
              onClick={() => { onPick(c); setQ(""); setRes([]); }}>
              <div className="font-medium">{c.ragione_sociale}</div>
              <div className="text-xs text-muted-foreground">{[c.piva, c.indirizzo_impianto, c.kwp && `${c.kwp} kWp`].filter(Boolean).join(" · ")}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ k, label, form, set, type = "number", sk, onToggle }) => {
  const on = !sk || isOn(form.servizi, sk);
  return (
    <div className={on ? "" : "opacity-60"}>
      <Label className="text-xs flex items-center gap-1.5">
        {sk && !/_soglia|_step/.test(k) && <Checkbox checked={on} onCheckedChange={(v) => onToggle(sk, !!v)} data-testid={`manut-servizio-${k}`} />}}
        <span className={on ? "" : "line-through"}>{label}</span>
      </Label>
      <Input type={type} step="any" disabled={!on} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`manut-field-${k}`} />
    </div>
  );
};

function Editor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({ ...f, servizi: { ...(f.servizi || {}), [k]: v } }));
  const tot = (isOn(form.servizi, "A") ? numOrNull(form.canone_a) || 0 : 0) + (isOn(form.servizi, "B") ? numOrNull(form.canone_b) || 0 : 0)
    + (form.extra || []).filter((x) => x.tipo !== "richiesta" && (x.descrizione || "").trim()).reduce((a, x) => a + (numOrNull(x.prezzo) || 0), 0);
  const save = async () => {
    if (!form.ragione_sociale?.trim()) { toast.error("Inserisci la ragione sociale del cliente"); return; }
    const body = { ...form, kwp: numOrNull(form.kwp) };
    SEZIONI.forEach(([, fs]) => fs.forEach(([k]) => { body[k] = numOrNull(form[k]); }));
    body.n_lavaggi = Math.min(12, Math.max(1, parseInt(form.n_lavaggi, 10) || 1));
    body.n_visite = Math.min(12, Math.max(1, parseInt(form.n_visite, 10) || 1));
    body.extra = (form.extra || []).map((x) => ({ ...x, prezzo: numOrNull(x.prezzo) }));
    setSaving(true);
    try {
      const r = form.id ? await api.put(`/manutenzioni/${form.id}`, body) : await api.post("/manutenzioni", body);
      toast.success("Preventivo salvato"); onSaved(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manut-editor">
        <DialogHeader><DialogTitle>{form.id ? `Preventivo ${form.numero}` : "Nuovo preventivo di manutenzione"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <section className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Cliente</div>
            <ClienteSearch onPick={(c) => setForm((f) => ({ ...f, ...c, kwp: c.kwp ?? f.kwp }))} />
            {form.lead_id && <div className="text-xs text-muted-foreground" data-testid="manut-lead-linked">Collegato al cliente CRM</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field k="ragione_sociale" label="Ragione sociale *" form={form} set={set} type="text" />
              <Field k="piva" label="P.IVA" form={form} set={set} type="text" />
              <Field k="indirizzo_impianto" label="Indirizzo sede impianto" form={form} set={set} type="text" />
              <Field k="kwp" label="Potenza impianto (kWp)" form={form} set={set} />
            </div>
          </section>
          <div className="text-xs text-muted-foreground">Togli la spunta ai servizi da non includere: nel PDF vengono tolti dal testo ed elencati nella sezione finale "Servizi esclusi (su richiesta)".</div>
          {SEZIONI.map(([title, fs], i) => (
            <section key={title} className="space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{title}</div>
              <div className="grid sm:grid-cols-3 gap-3">{fs.map(([k, l, sk]) => <Field key={k} k={k} label={l} form={form} set={set} sk={sk} onToggle={toggle} />)}</div>
              {i === 0 && <ServiziAB form={form} setForm={setForm} />}
              {i === 0 && <ExtraList form={form} setForm={setForm} />}
              {i === 0 && <div className="text-sm font-semibold" data-testid="manut-totale-canone">Totale canone annuo: {eur(tot)} + IVA</div>}
            </section>
          ))}
          <section className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Canone fatturato</Label>
              <select className="w-full h-9 rounded border bg-background px-2 text-sm" value={form.fatturazione_canone} onChange={(e) => set("fatturazione_canone", e.target.value)} data-testid="manut-field-fatturazione">
                <option value="alla stipula">alla stipula</option><option value="a intervento eseguito">a intervento eseguito</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Aggiornamento tariffe</Label>
              <select className="w-full h-9 rounded border bg-background px-2 text-sm" value={form.aggiornamento_tariffe} onChange={(e) => set("aggiornamento_tariffe", e.target.value)} data-testid="manut-field-aggiornamento">
                <option value="indice ISTAT">indice ISTAT</option><option value="variazione concordata">variazione concordata</option>
              </select>
            </div>
            <Field k="luogo_data" label="Luogo e data" form={form} set={set} type="text" />
          </section>
          <div>
            <Label className="text-xs">Note (facoltative, stampate prima delle firme)</Label>
            <Textarea value={form.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="manut-field-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="manut-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="manut-save">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Manutenzioni() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/manutenzioni", { params: { q } })).data); } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const openPdf = async (m) => {
    setPdfId(m.id);
    try {
      const r = await api.get(`/manutenzioni/${m.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error("Errore nella generazione del PDF"); } finally { setPdfId(null); }
  };
  const del = async (m) => {
    if (!window.confirm(`Eliminare il preventivo ${m.numero}?`)) return;
    try { await api.delete(`/manutenzioni/${m.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="manutenzioni-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2"><Wrench className="h-7 w-7 text-primary" /> Preventivi manutenzione</h1>
          <p className="text-sm text-muted-foreground mt-1">Preventivi di manutenzione ordinaria FIER O&amp;M, con PDF su carta intestata.</p>
        </div>
        <Button onClick={() => setEdit({ ...EMPTY })} data-testid="manut-new"><Plus className="h-4 w-4 mr-1" /> Nuovo preventivo</Button>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per cliente…" className="max-w-sm" data-testid="manut-search" />
      <Card className="rounded border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-3 py-2 text-left">Numero</th><th className="px-3 py-2 text-left">Data</th><th className="px-3 py-2 text-left">Cliente</th>
              <th className="px-3 py-2 text-right">kWp</th><th className="px-3 py-2 text-right">Canone annuo</th><th /></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-muted-foreground" data-testid="manut-empty">Nessun preventivo di manutenzione.</td></tr>
              : items.map((m) => (
                <tr key={m.id} className="border-t border-border hover:bg-secondary/40" data-testid={`manut-row-${m.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{m.numero}</td>
                  <td className="px-3 py-2 text-xs">{new Date(m.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-3 py-2"><div className="font-medium">{m.ragione_sociale}</div><div className="text-xs text-muted-foreground">{m.indirizzo_impianto}</div></td>
                  <td className="px-3 py-2 text-right">{m.kwp == null ? "—" : Number(m.kwp).toLocaleString("it-IT", { maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-right" data-testid={`manut-canone-${m.id}`}>{eur(m.totale_canone)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openPdf(m)} disabled={pdfId === m.id} data-testid={`manut-pdf-${m.id}`} title="PDF">
                      {pdfId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEdit(m)} data-testid={`manut-edit-${m.id}`} title="Modifica"><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(m)} data-testid={`manut-delete-${m.id}`} title="Elimina"><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>))}
          </tbody>
        </table>
      </Card>
      {edit && <Editor initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
